import { useNavigate } from "react-router-dom";

interface FeatureCardProps {
  imageSrc: string;
  imageAlt: string;
  title: string;
  description: string;
}

function FeatureCard({ imageSrc, imageAlt, title, description }: FeatureCardProps) {
  return (
    <div className="flex flex-col bg-card-gradient rounded-2xl overflow-hidden transition-all duration-300 group shadow-lg">
      {/* Card Image */}
      <div className="w-full aspect-[389/208] bg-card-dark overflow-hidden relative">
        <img
          src={imageSrc}
          alt={imageAlt}
          className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--hero-gray-start)] via-transparent to-transparent opacity-60" />
      </div>

      {/* Card Text */}
      <div className="p-6 md:p-8 flex flex-col gap-3 flex-1 justify-between">
        <div>
          <h3 className="text-lg md:text-xl font-bold text-white tracking-tight">
            {title}
          </h3>
          <p className="mt-2 text-sm text-gray-400 leading-relaxed">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LandingFeatures() {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-[1255px] mx-auto px-4 sm:px-6 md:px-8 py-12 md:py-20 flex flex-col gap-24 md:gap-36">
      {/* ─── SECTION 1: Trace every number to its source ─── */}
      <section className="flex flex-col items-center">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-[#15DE9B] text-xs sm:text-sm font-semibold tracking-wider uppercase">
            A fresh start
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mt-3">
            Trace every number to its source.
          </h2>
          <p className="text-sm sm:text-base text-gray-400 mt-4 leading-relaxed">
            Auditable company financials, filings, and official economic releases.
          </p>
        </div>

        {/* 3-Card Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 w-full mt-12 md:mt-16">
          <FeatureCard
            imageSrc="/assets/landing/audit-magnifier.png"
            imageAlt="Cell-Level Audit"
            title="Cell-Level Audit"
            description="One click reveals the raw fact, context, rule version, and exact SEC accession link."
          />
          <FeatureCard
            imageSrc="/assets/landing/xbrl-traceability.png"
            imageAlt="XBRL Tag-Level Traceability"
            title="XBRL Tag-Level Traceability"
            description="Go beyond document-level links to the exact raw XBRL coordinates."
          />
          <FeatureCard
            imageSrc="/assets/landing/bitemporal-history.png"
            imageAlt="Bitemporal History"
            title="Bitemporal History"
            description="See what the data is today, and exactly what was known on any historical date."
          />
        </div>
      </section>

      {/* ─── SECTION 2: Deep Dive (Text Left, Image Right) ─── */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6 flex flex-col items-start">
          <span className="text-[#15DE9B] text-xs sm:text-sm font-semibold tracking-wider uppercase">
            Unadulterated fruit
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mt-3 leading-tight">
            10+ years of
            <br />
            uncompromised history.
          </h2>

          <p className="mt-5 text-base sm:text-lg text-gray-300 leading-relaxed">
            A robust canonical model covering nonfinancial U.S. issuers, built for exact period semantics and comparability.
          </p>

          <div className="mt-8 space-y-4 text-sm sm:text-base text-gray-300">
            <div className="flex items-start gap-3">
              <span className="text-[#15DE9B] text-lg leading-tight font-bold shrink-0">•</span>
              <p className="leading-relaxed">
                <strong className="text-white font-semibold">Explicit Derivations:</strong> Clear handling of Q4, YTD, and TTM logic, never silently combining incompatible durations.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <span className="text-[#15DE9B] text-lg leading-tight font-bold shrink-0">◦</span>
              <p className="leading-relaxed">
                <strong className="text-white font-semibold">Restatement Visibility:</strong> Track amendment chains and effective knowledge dates effortlessly.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <span className="text-[#15DE9B] text-lg leading-tight font-bold shrink-0">◦</span>
              <p className="leading-relaxed">
                <strong className="text-white font-semibold">Export Ready:</strong> Fully reproducible CSV/Excel exports complete with metadata, methodology, and entitlement timestamps.
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/terminal")}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-medium border border-white/15 transition-all cursor-pointer"
          >
            Explore financial datasets &rarr;
          </button>
        </div>

        <div className="lg:col-span-6 flex justify-center">
          <div className="w-full max-w-[560px] aspect-square rounded-2xl overflow-hidden border border-[#2e2e2e] shadow-2xl relative group bg-card-dark">
            <img
              src="/assets/landing/cyber-globe.png"
              alt="Futuristic financial market data globe"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent pointer-events-none" />
          </div>
        </div>
      </section>

      {/* ─── SECTION 3: Deep Dive (Image Left, Text Right) ─── */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-6 order-2 lg:order-1 flex justify-center">
          <div className="w-full max-w-[560px] aspect-square rounded-2xl overflow-hidden border border-[#2e2e2e] shadow-2xl relative group bg-card-dark">
            <img
              src="/assets/landing/cyber-globe.png"
              alt="Financial data intelligence graph"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tl from-black/40 via-transparent to-transparent pointer-events-none" />
          </div>
        </div>

        <div className="lg:col-span-6 order-1 lg:order-2 flex flex-col items-start">
          <span className="text-[#15DE9B] text-xs sm:text-sm font-semibold tracking-wider uppercase">
            Audit-Grade Intelligence
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight mt-3 leading-tight">
            10+ years of
            <br />
            uncompromised history.
          </h2>

          <p className="mt-5 text-base sm:text-lg text-gray-300 leading-relaxed">
            A robust canonical model covering nonfinancial U.S. issuers, built for exact period semantics and comparability.
          </p>

          <div className="mt-8 space-y-4 text-sm sm:text-base text-gray-300">
            <div className="flex items-start gap-3">
              <span className="text-[#15DE9B] text-lg leading-tight font-bold shrink-0">•</span>
              <p className="leading-relaxed">
                <strong className="text-white font-semibold">Explicit Derivations:</strong> Clear handling of Q4, YTD, and TTM logic, never silently combining incompatible durations.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <span className="text-[#15DE9B] text-lg leading-tight font-bold shrink-0">◦</span>
              <p className="leading-relaxed">
                <strong className="text-white font-semibold">Restatement Visibility:</strong> Track amendment chains and effective knowledge dates effortlessly.
              </p>
            </div>

            <div className="flex items-start gap-3">
              <span className="text-[#15DE9B] text-lg leading-tight font-bold shrink-0">◦</span>
              <p className="leading-relaxed">
                <strong className="text-white font-semibold">Export Ready:</strong> Fully reproducible CSV/Excel exports complete with metadata, methodology, and entitlement timestamps.
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/terminal")}
            className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-medium border border-white/15 transition-all cursor-pointer"
          >
            Access research terminal &rarr;
          </button>
        </div>
      </section>

      {/* ─── BOTTOM CTA: Terminal Launch ─── */}
      <section className="rounded-3xl bg-card-gradient p-8 sm:p-14 text-center flex flex-col items-center gap-6 shadow-2xl">
        <span className="text-[#15DE9B] text-xs font-semibold tracking-wider uppercase">
          Ready to research?
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
          Enter the Combava Terminal
        </h2>
        <p className="text-gray-400 text-sm sm:text-base max-w-xl">
          Search thousands of public filings, view cell-level lineage, compare statements, and graph financial trends in real time.
        </p>
        <button
          onClick={() => navigate("/terminal")}
          className="px-8 py-3.5 text-sm font-bold text-black bg-white rounded-full hover:bg-gray-100 hover:shadow-[0_0_24px_rgba(255,255,255,0.3)] transition-all cursor-pointer whitespace-nowrap active:scale-95"
        >
          Open Terminal
        </button>
      </section>
    </div>
  );
}
